import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

/**
 * 층 도넛.
 *
 * ★ 입력은 `lensBlockedDonutSeries` 가 만든 `라벨|건수;라벨|건수` 문자열이다 —
 *   Flutter 쪽 커스텀 위젯이 리스트를 못 받아 그 모양이 됐고, 정답지가 그
 *   모양이라 **이식 1판에서는 그대로 받는다.**
 *
 * ⚠️⚠️ **중앙 라벨이 분모를 분자라고 말한 적이 있다**(CLAUDE.md 실측 —
 * 도넛 중앙이 `36건 · 진행 불가` 인데 KPI 는 `33.3%` 였다. 36은 **행동 턴**,
 * 즉 분모였다). → 중앙에는 **분모라는 사실을 적는다.**
 */

/**
 * 색은 **단색 농담 램프**다 — 범주 색을 순환시키지 않는다.
 *
 * ★ 원본 `LensDonut` 그대로다(`lens_donut.dart:24-29`): 축색(진행 불가 = 주황)
 *   `#E2603A` 에서 `#F7D3C6` 으로 선형 보간하고, **큰 조각일수록 진하다.**
 *
 * ⚠️⚠️ 범주 5색을 순환시키면 **조각이 6개를 넘는 순간 같은 색이 둘** 생긴다.
 * 층은 **여덟**이라 반드시 겹친다 — 실측으로 「판정 도달」과 「안전 차단」이
 * 같은 보라로 나왔다. ★ 램프는 그 문제가 구조적으로 없다.
 */
const RAMP_FROM = [0xe2, 0x60, 0x3a];
const RAMP_TO = [0xf7, 0xd3, 0xc6];

function shade(i: number, n: number): string {
  if (n <= 1) return `rgb(${RAMP_FROM.join(' ')})`;
  const t = i / (n - 1);
  const c = RAMP_FROM.map((from, k) => Math.round(from + (RAMP_TO[k]! - from) * t));
  return `rgb(${c.join(' ')})`;
}

export interface DonutSlice {
  label: string;
  value: number;
}

export function parseDonutSeries(series: string): DonutSlice[] {
  if (series.trim() === '') return [];
  return series
    .split(';')
    .map((p) => {
      const i = p.lastIndexOf('|');
      if (i < 0) return null;
      const value = Number.parseInt(p.slice(i + 1), 10);
      return Number.isNaN(value) ? null : { label: p.slice(0, i), value };
    })
    .filter((x): x is DonutSlice => x !== null);
}

export function Donut({
  series,
  centerValue,
  centerLabel,
}: {
  series: string;
  centerValue: string;
  /** ⚠️ **무엇의 수인지** 적는다. 분모를 분자처럼 쓰면 화면이 거짓말한다. */
  centerLabel: string;
}) {
  // ⚠️⚠️ **0건 조각을 걸러내지 않는다.** `lensBlockedDonutSeries` 가 일부러
  //    되살린 갈래이고, 범례에서 사라지면 「0건」인지 「안 세는지」 구분할 수
  //    없다. ★ 파이는 0 을 안 그리지만 **범례에는 남는다.**
  const slices = parseDonutSeries(series);
  const total = slices.reduce((a, s) => a + s.value, 0);
  if (total === 0) {
    return <p className="text-body-md text-muted-foreground">표본이 없습니다.</p>;
  }
  return (
    <div className="flex items-center gap-xl">
      <div className="relative h-48 w-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="62%"
              outerRadius="100%"
              // ⚠️ 12시부터 시계방향 — 집계가 정한 순서를 그대로 돌린다.
              startAngle={90}
              endAngle={-270}
              stroke="var(--lens-surface)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((s, i) => (
                <Cell key={s.label} fill={shade(i, slices.length)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v, n) => [`${Number(v)}건`, String(n)]}
              contentStyle={{
                background: 'var(--lens-surface)',
                border: '1px solid var(--lens-border)',
                borderRadius: 'var(--lens-radius-sm)',
                fontSize: 12,
                padding: '4px 8px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-title-lg tabular-nums">{centerValue}</span>
          <span className="text-label-sm text-muted-foreground">{centerLabel}</span>
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-xs">
        {slices.map((s, i) => (
          <li key={s.label} className="flex items-center gap-sm text-body-sm">
            <span
              className="h-2 w-2 shrink-0 rounded-pill"
              style={{ background: shade(i, slices.length) }}
            />
            <span className="min-w-0 flex-1 truncate">{s.label}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{s.value}건</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
